/**
 * What each client is owed this month, beside the content that answers it.
 *
 * The content plan used to open with totals of its own — reels, posts,
 * stories — counted from whatever pieces happened to be in the month, which
 * said nothing about what a client was actually promised and disagreed with
 * the client page as soon as a reel posted a day into the next month. This
 * reads the same requirements the client page does: "4 Reels" with how many
 * have a piece planned and how many have posted, so the one number a person
 * checks is the same number everywhere.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { Requirement } from '../context/AppDataContext'
import { cn } from '../lib/cn'
import { monthName } from '../lib/format'

/** Services and one-off builds are owed, but not as pieces on a calendar. */
const NOT_CONTENT = new Set(['active', 'project'])

export function isContentRequirement(r: Requirement): boolean {
  return !NOT_CONTENT.has(r.unit.toLowerCase())
}

export function shiftMonth(month: string, by: number): string {
  const d = new Date(`${month}T00:00`)
  d.setMonth(d.getMonth() + by, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

export function MonthStepper({ month, onChange, className }: { month: string; onChange: (m: string) => void; className?: string }) {
  return (
    <div className={cn('flex items-center gap-1 rounded-xl border border-shell-border bg-shell p-1', className)}>
      <button
        type="button"
        aria-label="Previous month"
        onClick={() => onChange(shiftMonth(month, -1))}
        className="rounded-lg p-1.5 text-shell-text hover:bg-shell-border/50"
      >
        <ChevronLeft size={16} />
      </button>
      <span className="min-w-[8.5rem] text-center text-sm font-semibold text-shell-text-active">{monthName(month)}</span>
      <button
        type="button"
        aria-label="Next month"
        onClick={() => onChange(shiftMonth(month, 1))}
        className="rounded-lg p-1.5 text-shell-text hover:bg-shell-border/50"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  )
}

function tone(r: Requirement): { box: string; note: string } {
  if (!isContentRequirement(r)) return { box: 'border-shell-border bg-shell-border/30 text-shell-text', note: 'Ongoing' }
  // More than was promised, first — before "all posted", which is true of an
  // overfull month too and was hiding it. A month holding nine reels against
  // a target of four is either free work or a piece filed in the wrong month,
  // and both are worth somebody looking at. The totals clamp to the target,
  // so without this nothing in the app said so at all.
  if (r.target_count > 0 && r.planned > r.target_count) {
    const over = r.planned - r.target_count
    return {
      box: 'border-rose-200 bg-rose-50 text-rose-800',
      note: `${over} more than promised`
    }
  }
  if (r.target_count > 0 && r.posted >= r.target_count) {
    return { box: 'border-emerald-200 bg-emerald-50 text-emerald-800', note: 'All posted' }
  }
  const toPlan = r.target_count - r.planned
  if (toPlan > 0) {
    return { box: 'border-amber-200 bg-amber-50 text-amber-900', note: `${toPlan} still to plan` }
  }
  return { box: 'border-sky-200 bg-sky-50 text-sky-800', note: `${r.target_count - r.posted} to post` }
}

export function RequirementsPanel({
  requirements,
  clientId,
  month
}: {
  requirements: Requirement[]
  /** One client's id, or `all`. */
  clientId: string
  month: string
}) {
  const shown = requirements.filter((r) => clientId === 'all' || r.client_id === clientId)
  const groups = new Map<string, Requirement[]>()
  for (const r of shown) groups.set(r.client_id, [...(groups.get(r.client_id) ?? []), r])

  const content = shown.filter(isContentRequirement)
  const target = content.reduce((n, r) => n + r.target_count, 0)
  const posted = content.reduce((n, r) => n + Math.min(r.posted, r.target_count), 0)
  const planned = content.reduce((n, r) => n + Math.min(r.planned, r.target_count), 0)
  const pct = target === 0 ? 0 : Math.round((posted / target) * 100)

  return (
    <section className="rounded-card border border-shell-border bg-shell p-4 shadow-card sm:p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-base font-semibold text-shell-text-active">Requirements</h2>
          <p className="text-xs text-shell-text">
            Owed in {monthName(month)}, planned and posted.
          </p>
        </div>
        {target > 0 && (
          <div className="flex items-center gap-4 text-xs text-shell-text">
            <span>
              <strong className="font-display text-lg text-shell-text-active tabular-nums">{planned}</strong>/{target} planned
            </span>
            <span>
              <strong className="font-display text-lg text-shell-text-active tabular-nums">{posted}</strong>/{target} posted
            </span>
            <span className="font-display text-lg font-bold text-shell-text-active tabular-nums">{pct}%</span>
          </div>
        )}
      </div>

      {target > 0 && (
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-shell-border/60" aria-hidden>
          <div className="relative h-full">
            <div className="absolute inset-y-0 left-0 rounded-full bg-sky-300/70" style={{ width: `${(planned / target) * 100}%` }} />
            <div className="absolute inset-y-0 left-0 rounded-full bg-emerald-500" style={{ width: `${(posted / target) * 100}%` }} />
          </div>
        </div>
      )}

      {groups.size === 0 ? (
        <p className="mt-3 text-sm text-shell-text">
          Nothing is owed in {monthName(month)} for {clientId === 'all' ? 'any client' : 'this client'}. Set what a client gets each month on their page, under the plan.
        </p>
      ) : (
        <ul className="mt-4 flex flex-col divide-y divide-shell-border/70">
          {[...groups.values()].map((rows) => {
            const first = rows[0]!
            const fromPlan = rows.every((r) => r.commitment_id === null)
            return (
              <li key={first.client_id} className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-start sm:gap-4">
                <span className="flex w-44 shrink-0 items-center gap-2 pt-1 text-sm font-medium text-shell-text-active">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: first.avatar_color }} />
                  <span className="truncate">{first.client_name}</span>
                </span>
                <div className="flex flex-wrap gap-2">
                  {rows.map((r) => {
                    const t = tone(r)
                    return (
                      <span
                        key={`${r.metric}-${r.commitment_id ?? 'plan'}`}
                        title={fromPlan ? 'From the standing plan — this month opens with it.' : undefined}
                        className={cn('flex items-center gap-2 rounded-lg border px-2.5 py-1 text-xs', t.box, fromPlan && 'border-dashed')}
                      >
                        <span className="font-medium">{r.metric}</span>
                        {isContentRequirement(r) && (
                          <span className="tabular-nums">
                            {r.posted}/{r.target_count}
                          </span>
                        )}
                        <span className="opacity-75">· {t.note}</span>
                      </span>
                    )
                  })}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
